import React, { useState, useEffect, useMemo, useRef } from "react";
import useApi from "../../hooks/useApiGetPost";
import {
  Chip,
  Button,
  Input,
  MenuItem,
  Select,
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
} from "@mui/material";
import VatReturnsFormPDF from "./VatReturnsFormPDF";

// Placeholder imports (assuming these exist in the actual project structure)
import useApiAthul from "../../hooks/useApi";
import groupSalesThreeLevels from "../utils/groupSalesThreeLevels";

import VATReportPDF from "./VATReportPDF";

import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import ReportDownloadButton from "../sales_analysis_report/ReportDownloadButton";
import DynamicTableForVatReport from "./DynamicTableForVatReport";
import * as XLSX from "xlsx";

// --- Utility Functions (Keep these in your main component or move to a shared file) ---

const formatNumber = (val) =>
  Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// The keys in your data are likely 'PROFIT', 'COST', 'QTY', 'VAT_AMT', etc.
// Ensure this utility function covers all numeric keys you want to total.
const isNumericHeader = (header) =>
  [
    "AMT",
    "COMMISSION",
    "RETURN",
    "VAT",
    "TOTAL",
    "PROFIT",
    "COST",
    "QTY",
    "AMOUNT",
    "SRNO",
    "VAT_AMT",
    "VATAMT",
    "VAT AMT",
    "TOTAL_AMT",
    "GROSS SALE",
    "DISC",
    "NET SALES",
    "EXCL_VAT_AMOUNT",
  ].some((s) => header.toUpperCase().includes(s));

// --- Helper Functions (Stubs for the original dynamic logic) ---

// Placeholder for logic that determines the key for the first column in aggregated modes
const getDescriptiveColumnKey = (mode, data) => {
  if (!data || data.length === 0) return "CODE";

  // In non-grouped/non-aggregated modes (like INVPROF or ITMPROF),
  // the first column might be the Invoice No or Item Code.
  if (mode === "SALES_CUSTOMER") return "ACCOUNT";
  if (mode === "SALES_SALESMAN") return "SALESPERSON";

  // Find the first key that is NOT a numeric header and NOT an internal grouping key
  const descriptiveKey = Object.keys(data[0]).find(
    (key) =>
      !isNumericHeader(key) &&
      !["isGroupHeader", "isSubHeader", "totalAmount"].includes(key),
  );

  // Fallback to the first non-numeric column found, or a default.
  return (
    descriptiveKey ||
    Object.keys(data[0]).find((key) => !isNumericHeader(key)) ||
    "CODE"
  );
};

// Placeholder for grandColumnTotals calculation (kept for PDF usage, but not passed to DynamicTable)
const calculateGrandTotals = (data) => {
  if (!data || data.length === 0) return {};
  const totals = {};
  const firstRow = data[0];

  Object.keys(firstRow).forEach((key) => {
    // Simple check: if the key contains numbers in the first row, aggregate it
    if (typeof firstRow[key] === "number" || isNumericHeader(key)) {
      totals[key] = data.reduce((sum, row) => sum + Number(row[key] || 0), 0);
    }
  });
  return totals;
};

// Placeholder for page and rowsPerPage logic (if you decide to implement this in the parent)
const usePagination = (data) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Slice the data for the current page
  const paginatedRows = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return {
    paginatedRows,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  };
};

const useScrollLoading = (data) => {
  // 25 is the default number of rows to load at once
  const defaultRowsPerPage = 1000;
  const [rowsToDisplay, setRowsToDisplay] = useState(defaultRowsPerPage);

  // Function to load the next set of data
  const loadNextPage = () => {
    setRowsToDisplay((prev) =>
      Math.min(prev + defaultRowsPerPage, data.length),
    );
  };

  // Reset the loaded count whenever the underlying filtered data array changes
  useEffect(() => {
    // Reset rows to display if the data is available, otherwise set to 0.
    setRowsToDisplay(data.length > 0 ? defaultRowsPerPage : 0);
  }, [data.length]);

  const paginatedRows = data.slice(0, rowsToDisplay);
  const hasMore = rowsToDisplay < data.length;

  return { paginatedRows, loadNextPage, hasMore, totalRows: data.length };
};

const VATReport = () => {
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : [{ API_CONFIG: "https://cubixweberp.com:213" }];
  let BASE_URL = "https://cubixweberp.com:213";
  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";

  console.log("API_PATHS -->", API_PATHS);
  if (API_PATHS) {
    if (API_PATHS?.length > 0) {
      BASE_URL = API_PATHS[0].API_CONFIG;
    }
  }
  const [reference, setReference] = useState("-");

  const [text_search_customer, setTextSearchCustomer] = useState("");

  const [searchTermCustomer, setSearchTerm] = useState("");

  const { request, loading } = useApiAthul();

  const [current_statusOfReturnCheckbox, setCurrentStatusOfReturnCheckbox] =
    useState("NORET");
  const [current_statusOfFifoCheckbox, setCurrentStatusOfFifoCheckbox] =
    useState(false);

  const [showItemList, setShowItemList] = useState(false);

  // --- State Variables ---
  const [searchTextTable, setSearchTextTable] = useState("");
  const [currentSelectedItem, setCurrentSelectedItem] = useState("-");
  const [modalSuggestions, setModalSuggestions] = useState([]);
  const [searchBy, setSearchBy] = useState("UNIVERSAL");

  const salesModes = [
    { label: "VAT - Report", value: "ALL" },
    { label: "VAT Input Only", value: "INPUT" },

    { label: "VAT Output Only", value: "OUTPUT" },

    { label: "Zero Rated Only", value: "ZERO" },

    { label: "Exempted Only", value: "VAT EXEMPTED" },

    { label: "Vat Summary", value: "VATSUMMARY" },

    { label: "FTA Form", value: "VATFTA" },
  ];

  const [currentSelectedModeObject, setCurrentSelectedModeObject] =
    useState(null);
  const [currentSelectedDepartment, setCurrentSelectedDepartment] =
    useState("-");
  const [userDepartmentList, setUserDepartment] = useState(null);
  const [currentSelectedCategory, setCurrentSelectedCategory] = useState("-");
  const [currentSelectedSubCategory, setCurrentSelectedSubCategory] =
    useState("-");
  const [currentSelectedGroup, setCurrentSelectedGroup] = useState("-");
  const [currentSelectedSubGroup, setCurrentSelectedSubGroup] = useState("-");

  const {
    loading: loadinggetSubCategory,
    data: datagetSubCategory,
    get: getSubCategory,
  } = useApi();
  const {
    loading: loadinggetCategory,
    data: datagetCategory,
    get: getCategory,
  } = useApi();
  const {
    loading: loadinggetSubgroup,
    data: datagetSubgroup,
    get: getSubgroup,
  } = useApi();
  const {
    loading: loadinggetgroup,
    data: datagetgroup,
    get: getgroup,
  } = useApi();
  const {
    loading: loadinggetCustomerList,
    data: datagetCustomerList,
    get: getCustomerList,
  } = useApi();

  const [customerListData, setCustomerListData] = useState(null);

  useEffect(() => {
    if (datagetCustomerList) {
      setCustomerListData(datagetCustomerList);
    }
  }, [datagetCustomerList]);

  const {
    data: datagetSalesOrderReportAPI,
    loading: loadinggetSalesOrderReportAPI,
    get: getSalesOrderReportAPI,
  } = useApi();
  const { data: datagetgetFinancialDateApi, get: getFinancialDateApi } =
    useApi();
  const { data: datagetJobList, get: getJobList } = useApi();
  const { data: datagetSalesmanList, get: getSalesmanList } = useApi();

  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [jobCode, setJobCode] = useState(null);
  const [chipSearch, setChipSearch] = useState("");
  const [selectedSalesMan, setSelectedSalesMan] = useState("");

  // --- Effects & Handlers ---

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    setUserDepartment(currentUser || null);

    setCurrentSelectedDepartment(
      currentUser?.[0]?.DEPTNO ? currentUser[0].DEPTNO : "-",
    );

    const today = new Date().toLocaleDateString("en-CA");
    setDateFrom(today);
    setDateTo(today);

    getCategory(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/category/-/-`,
    );
    getSubCategory(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/subcategory/-/-`,
    );
    getgroup(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/group/-/-`,
    );
    getSubgroup(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/subgroup/-/-`,
    );
    getJobList(
      `${API_PATHS[0].API_CONFIG}/api/cpayscount/${COMPANY_CODE}/joblist/-/-`,
    );

    if (text_search_customer?.trim()?.length != 0) {
      const currentUser = JSON.parse(localStorage.getItem("currentUser"));

      let DEPTNO = currentUser?.[0]?.DEPTNO ? currentUser[0].DEPTNO : "-";
      getCustomerList(
        `${API_PATHS[0].API_CONFIG}/api/Search_Customer/${COMPANY_CODE}/Cust/${text_search_customer}/${DEPTNO}`,
      );
    }

    let departmentToPassToAPI = "-";

    if (currentSelectedDepartment) {
      departmentToPassToAPI = currentSelectedDepartment;
    }

    getSalesmanList(
      `${API_PATHS[0].API_CONFIG}/api/MasterCount/${COMPANY_CODE}/SALESPERSONLIST/-/${departmentToPassToAPI}`,
    );

    handleChipClick({ label: "VAT - Report", value: "ALL" });
  }, []);

  const handleChipClick = (chip) => {
    setCurrentSelectedModeObject(chip);
    setSelectedFilters((prev) => (prev.includes(chip) ? [] : [chip]));
  };

  const resetFilters = () => {
    // FINANCIAL YEAR START DATE LOGIC
    if (datagetgetFinancialDateApi) {
      const fyStart = datagetgetFinancialDateApi[0]?.fystart?.split("T")[0];
      setDateFrom(fyStart);
    }

    const today = new Date().toLocaleDateString("en-CA");
    setDateTo(today);

    handleChipClick({ label: "VAT - Report", value: "ALL" });
    setItemSearch("");
    setCurrentSelectedGroup("-");
    setCurrentSelectedSubGroup("-");
    setCurrentSelectedCategory("-");
    setCurrentSelectedSubCategory("-");
    setJobCode(null);
    setChipSearch("");
  };

  const filteredChips = salesModes.filter((chip) =>
    chip.label.toLowerCase().includes(chipSearch.toLowerCase()),
  );

  useEffect(() => {
    if (
      currentSelectedModeObject &&
      dateFrom &&
      dateTo &&
      currentSelectedDepartment
    ) {
      let mode_to_pass_to_api = currentSelectedModeObject
        ? currentSelectedModeObject.value
        : "-";
      let job_code_to_pass_to_api = jobCode ? jobCode.JobCode : "-";
      let customer_account_to_api =
        currentSelectedItem != "-" ? currentSelectedItem?.account?.trim() : "-";

      let ret_status_for_api = current_statusOfReturnCheckbox;

      let fifo_status_for_api = "N";
      if (current_statusOfFifoCheckbox === true) {
        fifo_status_for_api = "N";
      } else {
        fifo_status_for_api = "N"; // what to pass - or anything else now put N just for testing
      }

      //let apiUrl = `${API_PATHS[0].API_CONFIG}/api/CRMPurchaseAnalysisrep/${COMPANY_CODE}/${mode_to_pass_to_api}/${dateFrom}/${dateTo}/${currentSelectedGroup}/${currentSelectedSubGroup}/${currentSelectedCategory}/${currentSelectedSubCategory}/-/-/${ret_status_for_api}/${currentSelectedDepartment}/${customer_account_to_api}/-/${fifo_status_for_api}`

      let apiUrl = `${API_PATHS[0].API_CONFIG}/api/CRMVatReport/${COMPANY_CODE}/${mode_to_pass_to_api}/${currentSelectedDepartment}/${reference}/-/-/${dateFrom}/${dateTo}/1900-01-01/1900-01-01?${Date.now()}`;

      getSalesOrderReportAPI(apiUrl);
    }
  }, [
    currentSelectedModeObject,
    dateFrom,
    dateTo,
    currentSelectedDepartment,
    reference,
  ]);

  useEffect(() => {
    getFinancialDateApi(
      `${API_PATHS[0].API_CONFIG}/api/Cpayscount/${COMPANY_CODE}/companyinfo/-/-`,
    );
  }, []);

  // FINANCIAL YEAR START DATE LOGIC
  useEffect(() => {
    if (datagetgetFinancialDateApi) {
      const fyStart = datagetgetFinancialDateApi[0]?.fystart?.split("T")[0];
      setDateFrom(fyStart);
    }
  }, [datagetgetFinancialDateApi]);

  const fetchProductData = async (
    query = "-",
    searchByValue = searchBy,
    location = "A",
  ) => {
    const formattedQuery = query.replace(/\//g, "%2F");
    try {
      const url = `${BASE_URL}/api/CRMStockList/${COMPANY_CODE}/${searchByValue}/${formattedQuery}/${location}/-`;
      const response = await request({ url, method: "GET", timeout: 5000 });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch product data", err);
      return [];
    }
  };

  const getInitialProductData = async () => {
    setModalSuggestions(await fetchProductData("-"));
  };
  useEffect(() => {
    getInitialProductData();
  }, []);

  const handleModalSearchQueryChange = async (query = "-") => {
    setItemSearch(query);
    setModalSuggestions(await fetchProductData(query));
  };

  useEffect(() => {
    if (searchTermCustomer?.length > 0) {
      //handleModalSearchQueryChange(itemSearch.trim());

      const currentUser = JSON.parse(localStorage.getItem("currentUser"));

      let DEPTNO = currentUser?.[0]?.DEPTNO ? currentUser[0].DEPTNO : "-";

      getCustomerList(
        `${API_PATHS[0].API_CONFIG}/api/Search_Customer/${COMPANY_CODE}/Cust/${searchTermCustomer}/${DEPTNO}`,
      );
    } else {
      setCurrentSelectedItem("-");
    }
  }, [searchTermCustomer]);

  const getTotalColumns = (data) => {
    if (!data || data.length === 0) return 1; // Default to 1 if no data

    // Dynamically determine all unique keys from the first row
    const allKeys = Object.keys(data[0]);

    // Exclude internal/helper keys used during grouping/aggregation
    const descriptiveColumnKey = getDescriptiveColumnKey(
      currentSelectedModeObject?.value,
      data,
    );

    const excludedKeys = [
      "isGroupHeader",
      "isSubHeader",
      "isSubSubHeader",
      "totalAmount",
      "totalAmountExclVAT",
      descriptiveColumnKey,
    ];

    // Count the descriptive column (1) + all other data columns
    const displayKeys = allKeys.filter((key) => !excludedKeys.includes(key));

    // The total is the sticky column + the rest of the dynamic columns
    return 1 + displayKeys.length;
  };

  // --- Core Logic (Grouping, Filtering, Totals) ---

  const reportData = datagetSalesOrderReportAPI || [];

  // The data passed to the child component must be the final, filtered/grouped array.
  const filteredRows = useMemo(() => {
    console.log("reportData inside  filteredRows---->", reportData);

    // 2. Simple Filtering (for all other modes)
    // Apply your existing searchTextTable filter logic here if needed, or pass the full data.
    let finalData = reportData;

    if (searchTextTable) {
      const search = searchTextTable.toLowerCase();
      finalData = reportData.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(search),
        ),
      );
    }

    return finalData;
  }, [reportData, currentSelectedModeObject, searchTextTable]);

  // Calculate the array of keys that should be totaled in the footer.
  const keysToTotal = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];

    // Get all keys from the first row
    const firstRow = reportData[0];
    const allKeys = Object.keys(firstRow);

    // Filter keys based on the existing numeric header logic (now including PROFIT, COST, QTY)
    // Ensure keys are filtered correctly.
    return allKeys.filter((key) => isNumericHeader(key));
  }, [reportData]);

  // Calculate the primary descriptive key (used for the first column and footer alignment)
  const descriptiveColumnKey = useMemo(() => {
    // Recalculate the descriptive key based on the final data, not just the raw reportData
    return getDescriptiveColumnKey(
      currentSelectedModeObject?.value,
      filteredRows,
    );
  }, [currentSelectedModeObject, filteredRows]);

  // Calculate totals only on the raw report data or final filtered data (Kept for PDF download logic)
  const grandColumnTotals = useMemo(() => {
    console.log(
      "calculateGrandTotals(reportData)",
      calculateGrandTotals(reportData),
    );
    return calculateGrandTotals(reportData);
  }, [reportData]);

  // --- Component Mapping ---

  const ReportTableMap = useMemo(
    () => ({
      // 1. GROUPED MODES (Hierarchical 3-level)
      INVPROF: DynamicTableForVatReport,
      ITMPROF: DynamicTableForVatReport,
    }),
    [],
  );

  const clickedOnPrint = async () => {
    console.log(
      "currentSelectedModeObject.value ",
      currentSelectedModeObject.value,
    );

    if (currentSelectedModeObject.value === "VATFTA") {
      // if (!filteredRows || filteredRows?.length === 0) return;
      if (!VatReturnsFormPDF) return;

      // Open a blank window first to avoid popup block
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert("Popup blocked! Please allow popups for this site.");
        return;
      }

      console.log(
        "A item N1 N2 new",
        filteredRows?.filter(
          (item) => item.Rowid?.trim().toUpperCase() === "B",
        )[0]?.N1,
        filteredRows?.filter(
          (item) => item.Rowid?.trim().toUpperCase() === "B",
        )[0]?.N2,
      );

      const getRawValue = (rowId, columnKey) => {
        const row = filteredRows?.filter(
          (item) => item.Rowid?.trim().toUpperCase() === rowId,
        )[0];
        return row ? row[columnKey] : 0;
      };

      const getFormattedValue = (rowId, columnKey) => {
        return formatNumber(getRawValue(rowId, columnKey));
      };

      // Helper function to safely get and format a total
      const getFormattedTotal = (totalValue) => {
        return formatNumber(totalValue);
      };

      // Retrieve Current User Configuration for Saudi Format Check
      const currentUser = JSON.parse(localStorage.getItem("currentUser")) || [];
      const isSaudiPhase1 = currentUser[0]?.VATMODE === "SAUDIPHASE1";

      // --- Totals Calculation (Needs to be formatted later) ---
      const totals_output = filteredRows
        ?.filter((item) => item.Rowid >= "A" && item.Rowid <= "M")
        .reduce(
          (acc, item) => ({
            N1: acc.N1 + (item.N1 || 0),
            N2: acc.N2 + (item.N2 || 0),
            N3: acc.N3 + (item.N3 || 0),
            N4: acc.N4 + (item.N4 || 0),
          }),
          { N1: 0, N2: 0, N3: 0, N4: 0 },
        );

      const totals_input = filteredRows
        ?.filter((item) => item.Rowid >= "P" && item.Rowid <= "Q")
        .reduce(
          (acc, item) => ({
            N1: acc.N1 + (item.N1 || 0),
            N2: acc.N2 + (item.N2 || 0),
            N3: acc.N3 + (item.N3 || 0),
            N4: acc.N4 + (item.N4 || 0),
          }),
          { N1: 0, N2: 0, N3: 0, N4: 0 },
        );

      // --- VAT on Sales and All Other Output (Table 1) ---
      let outputVat = [];
      let inputVat = [];

      if (isSaudiPhase1) {
        const standardRatedAmount = ["A", "B", "C", "D", "E", "F", "G"].reduce(
          (sum, id) => sum + getRawValue(id, "N1"),
          0,
        );
        const standardRatedVat = ["A", "B", "C", "D", "E", "F", "G"].reduce(
          (sum, id) => sum + getRawValue(id, "N2"),
          0,
        );

        outputVat = [
          {
            no: "1",
            category: "Standard rated sales",
            amount: getFormattedTotal(standardRatedAmount),
            vatAmount: getFormattedTotal(standardRatedVat),
            adjustment: "",
          },
          {
            no: "2",
            category:
              "Private Healthcare / Private Education sales to citizens",
            amount: "0.00",
            vatAmount: "0.00",
            adjustment: "",
          },
          {
            no: "3",
            category: "Zero rated domestic sales",
            amount: getFormattedValue("J", "N1"),
            vatAmount: getFormattedValue("J", "N2"),
            adjustment: "",
          },
          {
            no: "4",
            category: "Exports",
            amount: "0.00",
            vatAmount: "0.00",
            adjustment: "",
          },
          {
            no: "5",
            category: "Exempt sales",
            amount: getFormattedValue("K", "N1"),
            vatAmount: getFormattedValue("K", "N2"),
            adjustment: "",
          },
          {
            no: "6",
            category: "Total Sales",
            amount: getFormattedTotal(totals_output.N1),
            vatAmount: getFormattedTotal(totals_output.N2),
            adjustment: "",
          },
        ];

        inputVat = [
          {
            no: "7",
            category: "Standard rated domestic purchases",
            amount: getFormattedValue("P", "N1"),
            vatAmount: getFormattedValue("P", "N2"),
          },
          {
            no: "8",
            category: "Imports subject to VAT paid at customs",
            amount: getFormattedValue("L", "N1"),
            vatAmount: getFormattedValue("L", "N2"),
          },
          {
            no: "9",
            category:
              "Imports subject to VAT accounted for through reverse charge mechanism",
            amount: getFormattedValue("Q", "N1"),
            vatAmount: getFormattedValue("Q", "N2"),
          },
          {
            no: "10",
            category: "Zero rated purchases",
            amount: "0.00",
            vatAmount: "0.00",
          },
          {
            no: "11",
            category: "Exempt purchases",
            amount: "0.00",
            vatAmount: "0.00",
          },
          {
            no: "12",
            category: "Total purchases",
            amount: getFormattedTotal(totals_input.N1),
            vatAmount: getFormattedTotal(totals_input.N2),
          },
        ];
      } else {
        outputVat = [
          {
            no: "1.A",
            category: "Standard rate supplies in Abu Dhabi",
            amount: getFormattedValue("A", "N1"),
            vatAmount: getFormattedValue("A", "N2"),
            adjustment: "",
          },
          {
            no: "1.B",
            category: "Standard rate supplies in Dubai",
            amount: getFormattedValue("B", "N1"),
            vatAmount: getFormattedValue("B", "N2"),
            adjustment: "",
          },
          {
            no: "1.C",
            category: "Standard rate supplies in Sharjah",
            amount: getFormattedValue("C", "N1"),
            vatAmount: getFormattedValue("C", "N2"),
            adjustment: "",
          },
          {
            no: "1.D",
            category: "Standard rate supplies in Ajman",
            amount: getFormattedValue("D", "N1"),
            vatAmount: getFormattedValue("D", "N2"),
            adjustment: "",
          },
          {
            no: "1.E",
            category: "Standard rate supplies in Umm Al Quwain",
            amount: getFormattedValue("E", "N1"),
            vatAmount: getFormattedValue("E", "N2"),
            adjustment: "",
          },
          {
            no: "1.F",
            category: "Standard rate supplies in Ras Al Khaimah",
            amount: getFormattedValue("F", "N1"),
            vatAmount: getFormattedValue("F", "N2"),
            adjustment: "",
          },
          {
            no: "1.G",
            category: "Standard rate supplies in Fujairah",
            amount: getFormattedValue("G", "N1"),
            vatAmount: getFormattedValue("G", "N2"),
            adjustment: "",
          },
          {
            no: "2",
            category:
              "Tax Refunds provided to Tourists under the Tax Refunds for Tourists Scheme*",
            amount: getFormattedValue("H", "N1"),
            vatAmount: getFormattedValue("H", "N2"),
            adjustment: "",
          },
          {
            no: "3",
            category: "Supplies subject to the reverse charge provisions*",
            amount: getFormattedValue("I", "N1"),
            vatAmount: getFormattedValue("I", "N2"),
            adjustment: "",
          },
          {
            no: "4",
            category: "Zero rated supplies*",
            amount: getFormattedValue("J", "N1"),
            vatAmount: getFormattedValue("J", "N2"),
            adjustment: "",
          },
          {
            no: "5",
            category: "Exempt supplies*",
            amount: getFormattedValue("K", "N1"),
            vatAmount: getFormattedValue("K", "N2"),
            adjustment: "",
          },
          {
            no: "6",
            category: "Goods imported into the UAE*",
            amount: getFormattedValue("L", "N1"),
            vatAmount: getFormattedValue("L", "N2"),
            adjustment: "",
          },
          {
            no: "7",
            category: "Adjustments to goods imported into the UAE*",
            amount: getFormattedValue("M", "N1"),
            vatAmount: getFormattedValue("M", "N2"),
            adjustment: "",
          },
          {
            no: "8",
            category: "Totals",
            amount: getFormattedTotal(totals_output.N1),
            vatAmount: getFormattedTotal(totals_output.N2),
            adjustment: "",
          },
        ];

        inputVat = [
          {
            no: "9",
            category: "Standard rated Expenses *",
            amount: getFormattedValue("P", "N1"),
            vatAmount: getFormattedValue("P", "N2"),
          },
          {
            no: "10",
            category: "Supplies subject to the reverse charge provisions *",
            amount: getFormattedValue("Q", "N1"),
            vatAmount: getFormattedValue("Q", "N2"),
          },
          {
            no: "11",
            category: "Totals*",
            amount: getFormattedTotal(totals_input.N1),
            vatAmount: getFormattedTotal(totals_input.N2),
          },
        ];
      }

      // --- Net VAT Due ---
      let netVatDue = {
        totalDueTax: getFormattedTotal(totals_output.N2),
        totalRecoverableTax: getFormattedTotal(totals_input.N2),
        payableTax: getFormattedTotal(totals_output.N2 - totals_input.N2),
        excessRefundRequest:
          "Do you wish to request a refund for the above amount of excess recoverable tax",
      };

      // Render your PDF component
      const doc = (
        <VatReturnsFormPDF
          data={{ outputVat, inputVat, netVatDue }}
          isSaudiPhase1={isSaudiPhase1}
        />
      );

      // Generate PDF as blob
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();

      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);

      // Load the PDF in the blank window
      printWindow.location.href = blobUrl;

      // Optional: wait until the PDF loads, then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } else {
      if (!filteredRows || filteredRows?.length === 0) return;
      console.log("filteredRows", filteredRows);
      if (!VATReportPDF) return;

      // Open a blank window first to avoid popup block
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert("Popup blocked! Please allow popups for this site.");
        return;
      }

      // Render your PDF component
      const doc = (
        <VATReportPDF
          rows={filteredRows}
          descriptiveColumnKey={descriptiveColumnKey}
          grandColumnTotals={grandColumnTotals}
          currentSelectedModeObject={currentSelectedModeObject}
          clientDetails={{
            name: "test",
            address: "test address",
            phone: "1234567890",
            email: "test@email",
            footerText: "test footer",
          }}
        />
      );

      // Generate PDF as blob
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();

      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);

      // Load the PDF in the blank window
      printWindow.location.href = blobUrl;

      // Optional: wait until the PDF loads, then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    }
  };

  const CurrentTableComponent = currentSelectedModeObject
    ? DynamicTableForVatReport
    : null;

  // --- Pagination (Remains in Parent) ---
  // const { paginatedRows, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(filteredRows);

  const { paginatedRows, loadNextPage, hasMore, totalRows } =
    useScrollLoading(filteredRows);

  // const scrollContainerRef = useRef(null);

  // // --- Scroll Handler ---
  // const handleScroll = () => {

  //     console.log("handleScroll outside  --->")

  //     const currentTarget = scrollContainerRef.current;

  //     // Only trigger if we aren't already loading and there is more data to load
  //     if (!currentTarget || loadinggetSalesOrderReportAPI || !hasMore) return;

  //     const { scrollTop, scrollHeight, clientHeight } = currentTarget;

  //     console.log("handleScroll inside --->")

  //     // Check if user scrolled within 1px of the bottom
  //     if (scrollHeight - scrollTop <= clientHeight + 10) {
  //         loadNextPage();
  //     }
  // };

  const totalColumnsCount = getTotalColumns(paginatedRows); // Calculate the number

  const clickedOnExport = async () => {
    // Check if running on the client (browser) before attempting DOM operations
    if (typeof document === "undefined") {
      console.warn(
        "PDF Export called in a non-browser environment (SSR). Skipping download.",
      );
      return;
    }

    // 1. Render the PDF Document instance using the imported component
    const documentInstance = // Renamed to avoid confusion with browser's 'document'
      (
        <VATReportPDF
          rows={filteredRows}
          descriptiveColumnKey={descriptiveColumnKey}
          clientDetails={{
            name: "test",
            address: "test address",
            phone: "1234567890",
            email: "test@email",
            footerText: "test footer",
          }}
        />
      );

    try {
      // 2. Use the pdf() utility to generate the blob
      const blob = await pdf(documentInstance).toBlob();

      // 3. Create a temporary URL and link element to trigger the download
      const url = URL.createObjectURL(blob);

      // Use a single, correct declaration of 'link' (DOM operation is safe here)
      const link = document.createElement("a");

      // Define the file name
      link.download = `SalesReport_${new Date().toISOString().split("T")[0]}.pdf`;
      link.href = url;

      // 4. Append to body, click, and clean up
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up the URL object
    } catch (error) {
      console.error("Error during PDF generation or download:", error);
    }
  };

  const clickedOnExportExcel = () => {
    if (!filteredRows || filteredRows.length === 0) {
      alert("No data to export");
      return;
    }

    // Process data to exclude internal properties ('id', etc) if needed
    const exportData = filteredRows.map((row) => {
      const newRow = { ...row };
      delete newRow.isGroupHeader;
      delete newRow.isSubHeader;
      delete newRow.isSubSubHeader;
      delete newRow.totalAmount;
      delete newRow.totalAmountExclVAT;
      delete newRow.Rowid;
      return newRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "VAT Report");

    // Generate filename based on date and mode
    const modeName = currentSelectedModeObject?.label || "Report";
    const fileName = `VAT_Report_${modeName}_${new Date().toISOString().split("T")[0]}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div style={{ height: "calc(100vh - 200px)" }}>
      {/* Header panel Omitted for Brevity */}
      <div className="relative overflow-hidden rounded-xl shadow-md mx-4 my-1">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-blue-500 to-green-500 opacity-90" />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
          }}
        />
        <div className="relative px-6 py-6 sm:px-10 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-md">
              VAT Report
            </h1>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={clickedOnPrint}
              className="flex items-center px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-md text-sm font-medium transition duration-200 backdrop-blur-sm"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.8"
                stroke="currentColor"
                className="w-5 h-5 mr-1"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 9V2h12v7m-9 8h6m-9 4h12v-4H6v4zm-1 0a2 2 0 0 1-2-2V11a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5z"
                />
              </svg>
              Print
            </button>
            <button
              onClick={clickedOnExportExcel}
              className="flex items-center px-3 py-2 bg-white hover:bg-gray-100 text-gray-800 rounded-md text-sm font-medium transition duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.8"
                stroke="currentColor"
                className="w-5 h-5 mr-1 text-emerald-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 12-3-3m0 0-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                />
              </svg>
              Excel
            </button>
            {currentSelectedModeObject && filteredRows && (
              <ReportDownloadButton
                ReportPDF={VATReportPDF}
                currentSelectedModeObject={currentSelectedModeObject}
                filteredRows={filteredRows}
                descriptiveColumnKey={descriptiveColumnKey}
                grandColumnTotals={grandColumnTotals}
                clientDetails={{
                  name: "test",
                  address: "test address",
                  phone: "1234567890",
                  email: "test@email",
                  footerText: "test footer",
                }}
              >
                Download
              </ReportDownloadButton>
            )}
          </div>
        </div>
      </div>

      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            width: "18%",
            maxWidth: "260px",
            padding: 2,
            borderRight: "1px solid #ccc",
            backgroundColor: "#f9f9f9",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <div className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-1.5 shadow-sm hover:shadow-md transition-all duration-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 mb-3">
            <svg
              className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.7955 15.8111L21 21M18 10.5C18 14.6421 14.6421 18 10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search modes"
              value={chipSearch}
              onChange={(e) => setChipSearch(e.target.value)}
              className="w-full bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400 focus:ring-0"
            />
          </div>
          <div
            style={{
              flexGrow: 1,
              overflowY: "auto",
              maxHeight: "calc(100vh - 300px)",
              paddingRight: "4px",
            }}
            className="scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-500 transition-all duration-300"
          >
            {filteredChips.map((chip, index) => (
              <div key={index}>
                <Chip
                  title={chip.label}
                  label={chip.label}
                  clickable
                  color={
                    selectedFilters.filter(
                      (item) => item.value === chip.value,
                    )[0]?.value
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleChipClick(chip)}
                  sx={{
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    height: "auto",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      textAlign: "left",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                      width: "100%",
                      paddingTop: "2px",
                      paddingBottom: "2px",
                    },
                  }}
                />
              </div>
            ))}
          </div>
        </Box>

        {/* Right Panel (Main Content & Table) */}
        <Box
          sx={{
            width: "82%",
            paddingLeft: 3,
            paddingRight: 3,
            paddingTop: 2,
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Paper
            sx={{ padding: 1, borderRadius: "8px", backgroundColor: "#fff" }}
          >
            {/* Top Filters Omitted for Brevity */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div
                style={{
                  display: "flex",
                  marginLeft: "10px",
                  marginTop: "5px",
                }}
              >
                <Grid item>
                  <Input
                    fullWidth
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </Grid>
                <Grid item style={{ marginLeft: "10px" }}>
                  <Input
                    fullWidth
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </Grid>
              </div>

              <div
                style={{
                  display: "flex",
                  marginTop: "5px",
                  alignItems: "center",
                }}
              >
                <Select
                  fullWidth
                  value={currentSelectedDepartment}
                  onChange={(e) => setCurrentSelectedDepartment(e.target.value)}
                  displayEmpty
                  sx={{
                    height: 36,
                    fontSize: 14,
                    "& .MuiSelect-select": { paddingY: 0.5 },
                  }}
                >
                  <MenuItem value="-">Select Department</MenuItem>
                  {userDepartmentList?.map((group, index) => (
                    <MenuItem key={index} value={group.DEPTNO?.trim()}>
                      {group.DEPTNO?.trim()}
                    </MenuItem>
                  ))}
                </Select>

                <TextField
                  label="Reference"
                  variant="outlined"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  fullWidth
                  size="small"
                  style={{ marginLeft: "10px" }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "end" }}>
                <Box sx={{ marginRight: 3, display: "flex" }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={resetFilters}
                    sx={{
                      minWidth: "auto",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      lineHeight: 1,
                      borderRadius: "4px",
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setIsFilterPanelVisible((prev) => !prev)}
                    sx={{
                      marginLeft: "10px",
                      minWidth: "auto",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      lineHeight: 1,
                      borderRadius: "4px",
                    }}
                  >
                    Filters
                  </Button>
                </Box>
              </div>
            </div>

            {/* Expanded Filters */}
          </Paper>

          {/* Table Section */}
          <Paper
            sx={{
              width: "100%",
              marginTop: 2,
              // 4. 🚨 CRITICAL FIX: This container must take the remaining vertical space
              //    and control the flow of its children.
              flexGrow: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* 🚨 Dynamic Table Rendering Block */}
            <Box
              // ref={scrollContainerRef}
              sx={{
                p: 1,
                flexGrow: 1, // Take all available height
                overflowY: "auto", // Enable vertical scrolling here
                minHeight: 0, // Crucial for flex scrolling
              }}
              // onScroll={handleScroll} // 🚨 ADD SCROLL HANDLER
            >
              {CurrentTableComponent ? (
                <CurrentTableComponent
                  // Pass all necessary data and helpers to the selected component
                  rows={paginatedRows} // Use paginated rows for the component
                  loading={loadinggetSalesOrderReportAPI}
                  mode={currentSelectedModeObject.value} // Required by some children
                  // 🚀 FIX: Pass the array of column keys to total
                  footerTotalKeys={keysToTotal}
                  // Props specific to certain types
                  groupSalesThreeLevels={groupSalesThreeLevels} // For Grouped
                  descriptiveColumnKey={descriptiveColumnKey} // For Aggregated
                  totalColumnsCount={totalColumnsCount}
                  hasMore={hasMore}
                  loadNextPage={loadNextPage}
                />
              ) : (
                // Render a default state if no mode is selected
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="h6" color="textSecondary">
                    Please select a report mode from the left panel.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ paddingX: 2, paddingTop: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                Total Rows Found: {filteredRows.length}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </div>
  );
};

export default VATReport;
